import type { StorefrontProduct } from '../../api/client';
import { isThemePreview } from '../../api/previewVisit';
import { useProduct } from './productContext';
import { addressNamesProduct } from './ProductScope';
import { catalogRows, sampleProducts } from './sampleCatalog';
import { placeholderProducts } from './placeholderCatalog';
import { boolProp, numberProp, textProp, type ElementProps } from './elementProps';
import { useElementRender } from './elementRenderContext';
import { useListProducts, type ProductSource } from './useStoreCatalog';
import type { StoreRead } from './useStoreRead';
import { useListing } from './ListingContext';

/** What a product list draws: the products, the read behind them, how many, where from, and whether the next answer is on its way. */
export type ProductListRows = { rows: readonly StorefrontProduct[]; read: StoreRead<StorefrontProduct[]>; limit: number; from: ProductSource; stale: boolean };

// Reads the collection a list names, or the one the address names when it follows the page, or none for all.
function listHandle(props: ElementProps): string {
  const pathHandle = window.location.pathname.match(/^\/collections\/([^/]+)/)?.[1];
  return textProp(props, 'collection') || (boolProp(props, 'currentCollection') && pathHandle !== 'all' ? pathHandle ?? '' : '');
}

// Reads where a list's products come from: a search, the best sellers, the sale, or a collection (all products when none).
function sourceOf(props: ElementProps, query: string): ProductSource {
  const source = textProp(props, 'source');
  if (source === 'search') return { kind: 'search', query };
  if (source === 'listing') return { kind: 'listing' };
  if (source === 'best-selling' || source === 'on-sale') return { kind: source };
  if (source === 'under-price') return { kind: 'under-price', maxPrice: Math.max(0, numberProp(props, 'maxPrice', 200)) };
  return { kind: 'collection', handle: listHandle(props) };
}

// Says whether a list may fill with stand-ins: never for a search, nor for a collection the shop does not have.
const takesStandIns = (from: ProductSource, read: StoreRead<StorefrontProduct[]>): boolean =>
  from.kind === 'collection' && read.status !== 'missing';

// Reads the products a list draws, leaving out the product being viewed: one extra is read, then it is dropped.
export function useProductListRows(props: ElementProps): ProductListRows {
  const pageProduct = useProduct();
  const { store } = useElementRender();
  const from = sourceOf(props, store?.search?.query ?? '');
  const limit = numberProp(props, 'limit', 4);
  const viewing = addressNamesProduct() ? pageProduct?.id : undefined;
  const listing = useListing();
  const ownRead = useListProducts(from, viewing ? limit + 1 : limit);
  // A page's listing comes from the page; a listing grid placed on a page without one draws nothing.
  const read: StoreRead<StorefrontProduct[]> = from.kind !== 'listing' ? ownRead
    : listing ? { status: listing.read.status, data: (listing.read.data?.items ?? null) as StorefrontProduct[] | null } : { status: 'idle', data: null };
  const own = read.data ? read.data.filter((row) => row.id !== viewing).slice(0, limit) : read.data;
  // The theme's samples in a theme preview; an installed store's own products, and stand-ins where it has none.
  const standIns = takesStandIns(from, read) ? placeholderProducts(limit) : [];
  // A listing's count and pages are the store's, so a preview shows its samples only when the store has no products to list.
  const preview = isThemePreview() && (from.kind !== 'listing' || !own?.length);
  const rows = catalogRows(own, read.status !== 'loading', sampleProducts(props, limit), standIns, preview);
  return { rows, read, limit, from, stale: from.kind === 'listing' && (listing?.stale ?? false) };
}
